import { Capacitor } from '@capacitor/core';

/**
 * Analytics + Crashlytics 묶음. 호출 사이트는 한 함수만 알면 됨:
 *   Telemetry.logEvent('stage_clear', { stageId, stars });
 *   Telemetry.recordError(err, { scene: 'GameScene' });
 *
 * 웹에서는 console.log/warn 으로만 흘려 — 콘솔에서 이벤트 흐름 추적 가능.
 *
 * native 진입점 (NativeTelemetry) 은 src/services/FirebaseTelemetry.ts 로
 * 분리되어 동적 import 됨 — web 번들에 firebase-analytics/crashlytics 플러그인
 * 코드가 들어가지 않음. AuthService/Iap/AdReward 와 동일한 패턴.
 *
 * Crashlytics 는 비치명적 에러도 받음 → `recordError` 는 non-fatal 로 기록.
 * 진짜 크래시는 네이티브 코드가 알아서 캡처. `forceCrash` 는 α.6 검증용으로
 * 의도된 크래시를 강제로 발생시킴.
 */

const isNative = (): boolean => Capacitor.isNativePlatform();

/** native 어댑터 인터페이스 — FirebaseTelemetry.NativeTelemetry 와 매칭. */
interface NativeTelemetryAdapter {
  init(): Promise<void>;
  setUserId(uid: string): Promise<void>;
  logEvent(name: string, params?: Record<string, string | number | boolean>): Promise<void>;
  recordError(message: string, stack: string, context?: Record<string, string>): Promise<void>;
  forceCrash(): Promise<void>;
}

let nativeCache: Promise<NativeTelemetryAdapter> | null = null;

function getNative(): Promise<NativeTelemetryAdapter> {
  if (!nativeCache) {
    nativeCache = (async () => {
      // 동적 import — 웹 번들에는 capacitor-firebase analytics/crashlytics
      // 코드가 포함되지 않음.
      const { NativeTelemetry } = await import('./FirebaseTelemetry');
      // 명시적 타입 assignment — NativeTelemetry 시그니처 drift 시 컴파일 타임 실패.
      const inst: NativeTelemetryAdapter = new NativeTelemetry();
      return inst;
    })().catch((err) => {
      nativeCache = null;
      throw err;
    });
  }
  return nativeCache;
}

export async function init(): Promise<void> {
  if (!isNative()) return;
  try {
    const n = await getNative();
    await n.init();
  } catch (err) {
    console.warn('Telemetry.init failed', err);
  }
}

export async function setUserId(uid: string): Promise<void> {
  if (!isNative()) return;
  try {
    const n = await getNative();
    await n.setUserId(uid);
  } catch (err) {
    console.warn('Telemetry.setUserId failed', err);
  }
}

export async function logEvent(
  name: string,
  params?: Record<string, string | number | boolean>,
): Promise<void> {
  if (!isNative()) {
    console.log('[telemetry]', name, params);
    return;
  }
  try {
    const n = await getNative();
    await n.logEvent(name, params);
  } catch (err) {
    console.warn('Telemetry.logEvent failed', name, err);
  }
}

export async function recordError(
  err: unknown,
  context?: Record<string, string>,
): Promise<void> {
  const message = err instanceof Error ? err.message : String(err);
  const stack = err instanceof Error ? err.stack ?? '' : '';
  if (!isNative()) {
    console.warn('[crashlytics]', message, context, stack);
    return;
  }
  try {
    const n = await getNative();
    await n.recordError(message, stack, context);
  } catch (inner) {
    console.warn('Telemetry.recordError failed', inner);
  }
}

/** 테스트용 — α.6 검증에서 호출해 Crashlytics 콘솔에 강제 크래시 띄움. */
export async function forceCrash(): Promise<void> {
  if (!isNative()) {
    throw new Error('forceCrash invoked on web — Crashlytics not active');
  }
  const n = await getNative();
  await n.forceCrash();
}
